import React, { useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import axios from 'axios'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import { Grid, Box, Paper, Typography, TextField, Button } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import Title from '../components/atoms/Title'
import SelectInput from '../components/atoms/SelectInput'
import CenteredSubmitButton from '../components/molecules/CenteredSubmitButton'

import { Admin } from '../types/Admin'
import store from '../store'
import { setAuth } from '../store/actions/auth'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: `100%`
  },
  textField: {
    paddingRight: theme.spacing(1)
  }
}))

const AdminCMS: React.FC<
  RouteComponentProps<{ admin_id: string }> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>
> = ({ match, history, admin, setAuth }) => {
  const adminId = match.params.admin_id
  const currentPath = match.path
  const classes = useStyles({})
  const [type, setType] = useState<'create' | 'edit'>('create')
  const [adminData, setAdmin] = useState<{
    admin_id: string
    name: string
    password: string
    roles: string
    email: string
  }>({
    admin_id: '',
    name: '',
    password: '',
    roles: 'admin',
    email: ''
  })
  const [confirmPassword, setConfirmPassword] = useState<string>('')

  /**
   * 初期化処理
   */
  useEffect(() => {
    const fetchAdmin = async () => {
      const { data } = await axios.get<Admin>(`/admins/${adminId}`)
      setAdmin({
        admin_id: data.admin_id,
        name: data.name,
        password: data.password,
        roles: data.roles,
        email: data.email
      })
      setConfirmPassword(data.password)
    }
    if (currentPath === '/create-admin') {
      setType('create')
    } else {
      setType('edit')
      fetchAdmin()
    }
  }, [currentPath, adminId])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (adminData.password.length < 4) {
        alert('パスワードは半角英数字4文字以上を設定してください。')
        return
      }
      if (adminData.password !== confirmPassword) {
        alert('パスワードが確認用パスワードと異なっています。')
        return
      }
      if (type === 'create') {
        const { data } = await axios.get<Admin[]>('/admins')
        const exist = data.filter(d => d.admin_id === adminData.admin_id)
        if (exist.length > 0) {
          alert('既に登録されているログインIDは登録できません。')
          return
        }
        await axios.post(`/admins`, adminData)
      } else {
        await axios.put(`/admins/${adminId}`, adminData)
      }
      if (admin.adminId === adminData.admin_id) {
        setAuth(admin.id, admin.token, admin.adminId, admin.password, adminData.name)
      }
      history.push('/admins')
    } catch (error) {
      alert(error.message)
    }
  }

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Box display="flex" justifyContent="space-between">
            <Title>管理者の新規作成／編集</Title>
            <Box ml={3}>
              <Button color="primary" variant="outlined" href="/admins">
                一覧に戻る
              </Button>
            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Box>
              <Typography variant="subtitle2" color="error">
                ※は必須項目です。
              </Typography>
            </Box>
            <Box mb={5}>
              <Grid container alignItems="center" spacing={3}>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    権限{' '}
                    <Typography variant="caption" color="error">
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={11}>
                  <Box width={200}>
                    <SelectInput
                      value={adminData.roles}
                      options={[
                        { value: 'admin', label: 'admin' },
                        { value: 'general', label: 'general' }
                      ]}
                      label=""
                      selectHandler={select => setAdmin({ ...adminData, roles: select })}
                      fullWidth
                      required
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    表示名{' '}
                    <Typography variant="caption" color="error">
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={11}>
                  <Box width={500}>
                    <TextField
                      required
                      value={adminData.name}
                      variant="outlined"
                      label=""
                      placeholder="表示名を入力"
                      fullWidth
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setAdmin({ ...adminData, name: event.target.value })
                      }}
                      helperText="ユーザーへのお知らせ、メッセージ送信時にこの名前が表示されます。"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">E-mail</Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={11}>
                  <Box width={500}>
                    <TextField
                      value={adminData.email}
                      variant="outlined"
                      label=""
                      placeholder="E-mailを入力"
                      fullWidth
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setAdmin({ ...adminData, email: event.target.value })
                      }}
                      helperText="ユーザーからのメッセージ着信時にこのアドレスに通知が届きます。"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    ログインID{' '}
                    <Typography variant="caption" color="error">
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={11}>
                  <Box width={500}>
                    <TextField
                      required
                      value={adminData.admin_id}
                      variant="outlined"
                      label=""
                      placeholder="ログインIDを入力"
                      fullWidth
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setAdmin({ ...adminData, admin_id: event.target.value })
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    パスワード{' '}
                    <Typography variant="caption" color="error">
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={11}>
                  <Box width={500}>
                    <TextField
                      required
                      type="password"
                      value={adminData.password}
                      variant="outlined"
                      label=""
                      placeholder="パスワードを入力"
                      fullWidth
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setAdmin({ ...adminData, password: event.target.value })
                      }}
                      helperText="半角英数字4文字以上"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography variant="subtitle2">
                    パスワード確認{' '}
                    <Typography variant="caption" color="error">
                      ※
                    </Typography>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10} md={11}>
                  <Box width={500}>
                    <TextField
                      required
                      type="password"
                      value={confirmPassword}
                      variant="outlined"
                      label=""
                      placeholder="パスワードを再入力"
                      fullWidth
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setConfirmPassword(event.target.value)
                      }}
                      helperText="確認のためもう一度入力してください。"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box mb={10}>
              <CenteredSubmitButton
                submittable
                resettable
                submitHandler={() => {}}
                resetHandler={() => {
                  window.location.reload()
                }}
                size="large"
              />
            </Box>
          </form>
        </Paper>
      </Grid>
    </React.Fragment>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setAuth: (id: number, token: string, adminId: string, password: string, name: string) =>
      dispatch(setAuth(id, token, adminId, password, name))
  }
}

const mapStateToProps = (state: ReturnType<typeof store.getState>) => {
  return {
    admin: state.auth
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminCMS)
